import { getViewSwitcherSubMenuProps } from './getViewSwitcherSubMenuProps';
import { DefaultButton, IconButton } from '@fluentui/react/lib/Button';
import type { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import {
    addOrEditCalendarSavedView,
    deleteCalendarSavedView,
    changeCalendarSavedView,
} from 'owa-calendar-module-commanding';
import { CharmControl, CharmControlColorPattern } from 'owa-charmcontrol';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import loc from 'owa-localize';
import { getPalette } from 'owa-theme';
import * as React from 'react';
import {
    SaveCalendarViewConfig,
    lazyGetSavedCalendarViewsMap,
    lazyGetCurrentlyDisplayedViewId,
} from 'owa-save-calendar-view-data';
import {
    saveViewDeleteSubMenuText,
    saveViewEditSubMenuText,
} from 'owa-calendar-shared-strings/lib/strings/calendarSurfaceCommandBar.locstring.json';

const styles = require('../../components/CalendarModuleCommandBar.scss');

export function getSurfaceSavedViewMenuItems(
    onCustomRenderSubMenuChange: () => void
): FilterableMenuItem[] {
    if (!isFeatureEnabled('cal-saveCalendarView')) {
        return [];
    }

    const isSaveViewSelected = (savedView: SaveCalendarViewConfig) => {
        const getCurrentlyDisplayedViewId = lazyGetCurrentlyDisplayedViewId.tryImportForRender();
        return getCurrentlyDisplayedViewId && savedView.id == getCurrentlyDisplayedViewId();
    };

    const getSavedCalendarViewsMap = lazyGetSavedCalendarViewsMap.tryImportForRender();
    if (!getSavedCalendarViewsMap) {
        return [];
    }

    const getSavedViewsFilterableMenuItemOnRender = (
        savedView: SaveCalendarViewConfig
    ): ((item: any) => React.ReactNode) => item => {
        const savedViewSubmenuIcon = {
            iconName: ControlIcons.More,
            className: styles.viewMenuIcon,
        };
        const onSavedViewClick = () => {
            changeCalendarSavedView(savedView);
            onCustomRenderSubMenuChange();
        };
        const onDeleteSaveViewClick = (ev: React.MouseEvent<HTMLElement>) => {
            deleteCalendarSavedView(savedView);
            onCustomRenderSubMenuChange();
        };
        const onEditSaveViewClick = (ev: React.MouseEvent<HTMLElement>) => {
            addOrEditCalendarSavedView(savedView, true /*shouldEdit*/);
            onCustomRenderSubMenuChange();
        };
        const savedViewSubMenuItems: IContextualMenuItem[] = [
            {
                text: loc(saveViewEditSubMenuText),
                key: loc(saveViewEditSubMenuText),
                title: loc(saveViewEditSubMenuText),
                onClick: onEditSaveViewClick,
            },
            {
                text: loc(saveViewDeleteSubMenuText),
                key: loc(saveViewDeleteSubMenuText),
                title: loc(saveViewDeleteSubMenuText),
                onClick: onDeleteSaveViewClick,
            },
        ];

        const palette = getPalette();
        const charmColorProps = {
            colorPattern: CharmControlColorPattern.Normal,
            calPrimary: palette.themePrimary,
            calDark: palette.themeDark,
            calDarkAlt: palette.themeDarkAlt,
            textColor: palette.neutralPrimary,
        };

        return (
            <div>
                <DefaultButton
                    className={styles.savedViewDefaultButton}
                    onClick={onSavedViewClick}
                    ariaLabel={savedView.viewName}
                    styles={{
                        label: styles.viewMenuButtonLabel,
                        flexContainer: styles.savedViewFlexContainer,
                    }}>
                    <CharmControl
                        iconId={parseInt(savedView.viewIcon)}
                        className={styles.charm}
                        colorProps={charmColorProps}
                    />
                    <span className={styles.savedViewName}>{savedView.viewName}</span>
                </DefaultButton>
                <IconButton
                    menuProps={getViewSwitcherSubMenuProps(savedViewSubMenuItems)}
                    menuIconProps={savedViewSubmenuIcon}
                    className={styles.savedViewIconButton}
                />
            </div>
        );
    };

    const savedViewsMapValues = getSavedCalendarViewsMap().values();
    const savedViews: FilterableMenuItem[] = [];
    for (let savedView of savedViewsMapValues) {
        savedViews.push(
            new FilterableMenuItem({
                [FilterableMenuItemArgs.key]: savedView.id,
                [FilterableMenuItemArgs.onRender]: getSavedViewsFilterableMenuItemOnRender(
                    savedView
                ),
                [FilterableMenuItemArgs.title]: savedView.viewName,
                [FilterableMenuItemArgs.className]: isSaveViewSelected(savedView)
                    ? styles.viewOptionSelected
                    : null,
            })
        );
    }
    if (!savedViews.length) {
        return [];
    }
    return [
        new FilterableMenuItem({
            [FilterableMenuItemArgs.key]: 'SavedViewDivider',
            [FilterableMenuItemArgs.name]: '-',
        }),
        ...savedViews,
    ];
}
